import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { logEvent } from '@amplitude/analytics-browser';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Collapse from '@mui/material/Collapse';
import { useLazyTranslation } from '../../hooks/i18n';
import { getSidebarEvent } from '../../constants/events';
import { COLORS } from '../../themev2';
import Typography from '../Typography';
import Tooltip from '../Tooltip';

export type AppMenuItemType = {
  name: string;
  route: string;
  Icon: React.FunctionComponent<any> | null;
  trackingName: string;
  isAbsoluteRoute?: boolean;
  permissions?: string[];
};

export type AppMenuItemProps = AppMenuItemType & {
  items?: AppMenuItemType[];
  onClickItemCallback: () => void;
  isDrawerOpened?: boolean;
  isChildren?: boolean;
};

const AppMenuItem = ({
  name,
  route,
  Icon,
  trackingName,
  isAbsoluteRoute = false,
  items = [],
  onClickItemCallback,
  isDrawerOpened,
  isChildren = false,
}: AppMenuItemProps) => {
  const isExpandable = items && items.length > 0;
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useLazyTranslation();

  const isContained =
    location.pathname.startsWith(route) ||
    items.some((child) => location.pathname.startsWith(child.route));
  const isActive = route === location.pathname || (isExpandable && !isDrawerOpened && isContained);
  const isElementOpen = isActive || (isExpandable && isContained);

  const handleListItemClick = () => {
    logEvent(getSidebarEvent(trackingName));
    if (isAbsoluteRoute) {
      window.open(route, '_blank');
    }
    navigate(route);
    onClickItemCallback();
  };

  const MenuItemRoot = (
    <ListItem
      onClick={handleListItemClick}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
        backgroundColor: 'transparent',
        padding: isChildren ? '2px 8px 2px 18px' : '4px 8px',
        borderRadius: '8px',
        display: 'flex',
        gap: isDrawerOpened ? '12px' : '0px',
        alignItems: 'center',
        justifyContent: isDrawerOpened ? 'flex-start' : 'center',
        minHeight: isDrawerOpened ? '28px' : '37px',
      }}
    >
      {!!Icon && (
        <Tooltip title={t(name)} placement="right" disableHoverListener={isDrawerOpened}>
          <ListItemIcon
            sx={{
              color: isActive ? COLORS.brand.green : COLORS.brand.paper,
              minWidth: 0,
            }}
          >
            <Icon fillColor={isActive ? COLORS.brand.green : COLORS.brand.paper} />
          </ListItemIcon>
        </Tooltip>
      )}
      {isDrawerOpened && (
        <ListItemText>
          <Typography
            sx={{
              color: isActive ? COLORS.brand.green : COLORS.brand.paper,
              fontSize: '14px',
              fontWeight: isChildren ? '400' : '600',
              lineHeight: '150%',
              display: isDrawerOpened ? 'initial' : 'none',
              textWrap: 'nowrap',
            }}
          >
            {t(name)}
          </Typography>
        </ListItemText>
      )}
    </ListItem>
  );

  const MenuItemChildren =
    isExpandable && isDrawerOpened ? (
      <Collapse in={isElementOpen} timeout="auto" unmountOnExit>
        <List sx={{ padding: '0' }}>
          {items.map((item, index) => (
            <AppMenuItem
              {...item}
              key={index}
              onClickItemCallback={onClickItemCallback}
              isDrawerOpened={isDrawerOpened}
              isChildren={true}
            />
          ))}
        </List>
      </Collapse>
    ) : null;

  return (
    <>
      {MenuItemRoot}
      {MenuItemChildren}
    </>
  );
};

export default AppMenuItem;
